import React from 'react';
import type { CommaDelimitedLocationType, Provider } from './types';
import { SingleDropdown } from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import type { Result as LocationResult } from '@grnhse/location-control/dist/src/common/types';
import { useAutocompleteDropdown } from './hooks/use_autocomplete_dropdown';
import { useFetchLocations } from './hooks/use_fetch_locations';
import { useSelectedLocation } from './hooks/use_selected_location';
import { type LabelProps } from '@grnhse/seedling/lib/birch/utils/constants';
import { useResultSelected } from './hooks/use_result_selected';

type Props = {
  clearable?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  location: LocationResult | null;
  locationType?: CommaDelimitedLocationType;
  name?: string;
  onChange: (location: LocationResult | null) => void;
  persisted?: boolean;
  placeholder?: string;
  provider?: Provider;
  required?: boolean;
} & LabelProps;

export const LocationInput = ({
  clearable,
  disabled,
  fullWidth = false,
  location,
  locationType = 'city',
  name,
  onChange,
  persisted = true,
  placeholder,
  provider,
  required = false,
  ...labelProps
}: Props) => {
  const selectedLocation = useSelectedLocation(location);
  const fetchItems = useFetchLocations(locationType, provider);
  const resultSelected = useResultSelected(locationType, provider);

  const autocompleteDropdownProps = useAutocompleteDropdown<LocationResult>({
    fetchItems: async (term: string) => {
      // This is a workaround because Seedling's dropdown will make an additional request after select.
      // We can save on our request count by skipping a request if the term matches what we already have.
      if (selectedLocation?.label === term) {
        return {
          items: [],
        };
      }

      return fetchItems(term);
    },
    onSelectedItemChange: async ({ selectedItem }) => {
      if (selectedItem?.value && persisted) {
        await resultSelected(selectedItem.value);
      }

      onChange(selectedItem?.value || null);
    },
    selectedItem: selectedLocation,
  });

  const dataProvidesPrefix = name || 'location';

  return (
    <SingleDropdown dropdownProps={autocompleteDropdownProps}>
      <SingleDropdown.Field
        {...labelProps}
        data-provides={`${dataProvidesPrefix}-field`}
        disabled={disabled}
        fullWidth={fullWidth}
        name={name}
        clearable={clearable}
        placeholder={placeholder}
        required={required}
      />
      <SingleDropdown.Flyout data-provides={`${dataProvidesPrefix}-flyout`} />
    </SingleDropdown>
  );
};

LocationInput.displayName = 'LocationInput';
